// @flow
import { atom, useRecoilValue, type RecoilState } from 'recoil';
import {
  defaultLanguage,
  type LanguageType,
} from './validation/static-analysis/types.js';

export const languageState: RecoilState<LanguageType> = atom<LanguageType>({
  key: 'language',
  default: defaultLanguage,
});

const languageToComponent = {
  English: English,
  Spanish: Spanish,
};

// A proof for the type system that we provide a mapping for every type in the
// LanguageType union, and no mappings beyond that. We use this theorem:
//   If A ⊆ B ∧ B ⊆ A, then a = b
type MapKeys = $Keys<typeof languageToComponent>;
(x: LanguageType): MapKeys => x;
(x: MapKeys): LanguageType => x;

export function Language({
  children,
}: {|
  children: [React$Element<typeof English>, React$Element<typeof Spanish>],
|}): React$Node {
  const language = useRecoilValue(languageState);
  const componentType = languageToComponent[language];
  const element = children.find((child) => child.type === componentType);
  if (element === undefined) {
    throw Error(
      `Invariant violated: No suitable child for language "${language}" was provided`,
    );
  }
  return element;
}

// These tag props are critical to keep, but shouldn't actually be used at runtime.
// They are a way to get nominal typing for these components (rather than structural
// typing) so that Flow can enforce the correct invariant for Language children.
type EnglishProps = {| children: React$Node, tag?: 'English' |};
export function English(props: EnglishProps): React$Node {
  return props.children;
}

type SpanishProps = {| children: React$Node, tag?: 'Spanish' |};
export function Spanish(props: SpanishProps): React$Node {
  return props.children;
}

// Occasionally we need to provide translations in a location that does not
// support taking a React element. For example, consider the title prop:
//
//   <button title={...} />
//
// Here, title must be of type string, not of type React$Element.
//
// For these rare cases we can use this hook like so:
//
//   const title = useLanguageSpecificValue({
//     English: 'Some english words',
//     Spanish: 'Algunas palabras en español',
//   });
//   return <button title={title}>button</button>;
//
export function useLanguageSpecificValue(languageToValue: LanguageToValue) {
  const language = useRecoilValue(languageState);
  const value = languageToValue[language];
  return value;
}

type LanguageToValue = {|
  English: mixed,
  Spanish: mixed,
|};
// A proof for the type system that we provide a mapping for every type in the
// LanguageType union, and no mappings beyond that. We use this theorem:
//   If A ⊆ B ∧ B ⊆ A, then a = b
type LanguageToValueKeys = $Keys<LanguageToValue>;
(x: LanguageType): LanguageToValueKeys => x;
(x: LanguageToValueKeys): LanguageType => x;
